import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PartyPopper, CheckCircle, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const CongratulationsModal = ({ open, onClose, productTitle, amount, productImage, token, API }) => {
  const [whatsapp, setWhatsapp] = useState("+507 ");
  const [loading, setLoading] = useState(false);
  const [whatsappSaved, setWhatsappSaved] = useState(false);

  const handleSaveWhatsapp = async () => {
    if (!whatsapp.trim() || whatsapp.length < 10) {
      toast.error("Por favor ingresa un número de WhatsApp válido");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/update-whatsapp`,
        { whatsapp: whatsapp.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success("¡WhatsApp guardado!");
      setWhatsappSaved(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar WhatsApp");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <PartyPopper className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <DialogTitle className="text-2xl text-center">¡Felicidades!</DialogTitle>
          <DialogDescription className="text-center text-base">
            Por tu producto adquirido
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {productTitle && (
            <div className="bg-[#F1F5F9] rounded-xl p-4">
              <div className="flex items-center gap-4">
                {productImage && (
                  <img 
                    src={productImage} 
                    alt={productTitle}
                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#64748B]">Producto</p>
                  <p className="font-semibold text-[#0F172A] truncate">{productTitle}</p>
                  {amount && <p className="text-lg font-bold text-[#10B981]">${amount.toLocaleString()}</p>}
                </div>
              </div>
            </div>
          )}

          {/* WhatsApp Input - Only show if not saved yet */}
          {!whatsappSaved && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
              <Label htmlFor="winner-whatsapp" className="flex items-center gap-2 text-green-800 font-medium">
                <Phone className="h-4 w-4 text-[#34D399]" />
                Tu WhatsApp para coordinar entrega
              </Label>
              <Input
                id="winner-whatsapp"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="+507 6123 4567"
                className="rounded-xl border-green-300 focus:border-green-500"
                data-testid="winner-whatsapp-input"
              />
              <Button
                onClick={handleSaveWhatsapp}
                disabled={loading || whatsapp.length < 10}
                className="w-full rounded-full bg-[#34D399] hover:bg-[#10B981] text-white"
                data-testid="save-whatsapp-btn"
              >
                {loading ? (
                  <div className="spinner" style={{ width: 20, height: 20 }} />
                ) : (
                  "Guardar WhatsApp"
                )}
              </Button>
            </div>
          )}

          {whatsappSaved && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm text-green-800 font-medium">WhatsApp guardado correctamente</p>
            </div>
          )}
          
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
            <p className="text-sm font-medium text-[#0F172A]">📋 Próximos pasos:</p>
            
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  Te contactaremos por <strong>WhatsApp</strong> para coordinar la entrega y revisión del producto.
                </p>
              </div>

              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  <strong>Pago contraentrega:</strong> Revisas el producto en persona y solo pagas si estás conforme.
                </p>
              </div>
              
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  El precio <strong>NO ES NEGOCIABLE</strong>. Si el producto no cumple con lo anunciado, puedes cancelar sin costo.
                </p>
              </div>
              
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  Pagos aceptados: <strong>Yappy</strong> o <strong>BAC</strong>.
                </p>
              </div>

              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-green-800">
                  💰 ¡Deja una reseña después de tu compra y <strong>gana mínimo $2 de cashback</strong>!
                </p>
              </div>
            </div>
          </div>
          
          <p className="text-center text-sm text-[#64748B]">
            Gracias por ser parte de <span className="font-semibold text-[#475569]">Clueer</span>!
          </p>
        </div>
        
        <DialogFooter>
          <Button 
            onClick={handleClose} 
            className="w-full rounded-full bg-[#475569] hover:bg-[#334155] h-12"
            data-testid="close-congratulations"
          >
            Entendido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CongratulationsModal;
